import { Wallet } from '@coral-xyz/anchor'
import type { WalletContextState } from '@solana/wallet-adapter-react'
import {
  Connection,
  Keypair,
  PublicKey,
  Transaction,
  VersionedTransaction
} from '@solana/web3.js'
import { SOLANA_RPC_ENDPOINT } from 'app/solana-config'
import {
  assertPlayWallet,
  selectedWalletAddressVar
} from 'shared/store/play-wallet'
import { assertSolForFee, explainNotEnoughSol } from './sol-fee'

/**
 * Every game transaction is sent through this connection (Anchor providers,
 * token-account creation, backend-built transactions), so a fee payer without
 * SOL gets one clear message and the Deposit dialog instead of raw RPC text.
 */
class GameConnection extends Connection {
  override async sendRawTransaction(
    ...args: Parameters<Connection['sendRawTransaction']>
  ) {
    try {
      return await super.sendRawTransaction(...args)
    } catch (error) {
      // NOTE: game transactions are signed only by the wallet selected for play
      throw explainNotEnoughSol(error, selectedWalletAddressVar())
    }
  }
}

export const connection: Connection = new GameConnection(SOLANA_RPC_ENDPOINT, {
  commitment: 'confirmed'
})

/** The checks every game transaction passes before the wallet signs it. */
export const assertCanSign = async (wallet: WalletContextState) => {
  assertPlayWallet(wallet.publicKey?.toBase58())
  await assertSolForFee(connection, wallet.publicKey as PublicKey)
}

export function walletAdapterToAnchorWallet(
  wallet: WalletContextState
): Wallet {
  return {
    publicKey: wallet.publicKey as PublicKey,
    payer: Keypair.generate(),
    signTransaction: async <T extends Transaction | VersionedTransaction>(
      tx: T
    ): Promise<T> => {
      if (!wallet.signTransaction) {
        throw new Error('Wallet does not support signTransaction')
      }
      await assertCanSign(wallet)
      const signed = await wallet.signTransaction(tx as Transaction)
      return signed as T
    },
    signAllTransactions: async <T extends Transaction | VersionedTransaction>(
      txs: T[]
    ): Promise<T[]> => {
      if (!wallet.signAllTransactions) {
        throw new Error('Wallet does not support signAllTransactions')
      }
      await assertCanSign(wallet)
      const signed = await wallet.signAllTransactions(txs as Transaction[])
      return signed as T[]
    }
  }
}
